import React, { useEffect, useRef, useState, memo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Lightbox from "react-image-lightbox";
import Img from "react-image";
import get from "lodash/get";

import { isVideoLink } from "../../../util/common";
import { selectCdnImageSettings } from "../../../util/selectors";
import { useIsRTL } from "../../../hooks";
import useSwiperScript from "@/hooks/useSwiperScript";
import { useAppSelector } from "@/redux/store";
import { selectProductDetails } from "../selectors";

const MobileImage = ({ imageList }) => {
  const [activeSlide, setActiveSlide] = useState(null);
  const [videoIndex, setVideoIndex] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingImage, setisLoadingImage] = useState(true);
  const loadedScriptStatus = useSwiperScript();
  const [swiper, setSwiper] = useState(null);
  const product = useAppSelector(selectProductDetails);

  const mobileVideoRefs = useRef([]);
  const mobSlider = useRef(null);

  const configCdnImagesSettings = useSelector(selectCdnImageSettings);

  const { state: locationState } = useLocation<{ fromPLP?: boolean }>();
  const isFromPLP = locationState?.fromPLP;

  const checkFirstVideoURL = () => {
    const videoIndex = imageList?.findIndex(item => isVideoLink(item));
    if (videoIndex && videoIndex !== -1) {
      setVideoIndex(videoIndex);
    }
  };

  useEffect(() => {
    checkFirstVideoURL();
  }, []);
  const lightboxMainSrc = `${configCdnImagesSettings?.main}${imageList[photoIndex]}`;

  const isLastImage =
    imageList.length - 1 === photoIndex && imageList.length > 1;
  const lightboxNextSrc = !isLastImage
    ? get(
        imageList,
        imageList.length > photoIndex ? photoIndex + 1 : photoIndex,
        ""
      )
    : imageList[0];

  const isFirstImage = photoIndex === 0 && imageList.length > 1;
  const lightboxPrevSrc = !isFirstImage
    ? get(imageList, photoIndex >= 0 ? photoIndex - 1 : photoIndex, "")
    : imageList[imageList.length - 1];

  const lightboxOnCloseRequest = () => {
    swiper.slideToLoop(photoIndex, 0);
    setIsOpen(false);
  };

  const lightboxOnMovePrevRequest = () => {
    setPhotoIndex(!isFirstImage ? photoIndex - 1 : imageList.length - 1);
  };

  const lightboxOnMoveNextRequest = () => {
    setPhotoIndex(!isLastImage ? photoIndex + 1 : 0);
  };

  const handleSwipe = () => {
    if (activeSlide === videoIndex) {
      setActiveSlide("");
    }
  };

  const handleActiveVideoSlide = () => {
    mobSlider.current.slickGoTo(videoIndex);
    setActiveSlide(videoIndex);
  };

  const handleFullScreenImage = id => {
    setIsOpen(true);
    setPhotoIndex(id);
    setActiveSlide(id);
  };

  const handleMobVideo = (nextSlide, children) => {
    if (nextSlide === videoIndex) {
      setActiveSlide(videoIndex);
    }
    const nextVideo = mobileVideoRefs.current[nextSlide];

    if (children[nextSlide] && children[nextSlide].type === "video") {
      nextVideo.play();
    }
  };

  useEffect(() => {
    if (mobSlider.current && (loadedScriptStatus || window.Swiper)) {
      const swiper = new window.Swiper(mobSlider.current, {
        loop: true,
        slidesPerView: 1,
        slidesPerGroup: 1,
        pagination: {
          el: ".swiper-pagination",
          clickable: true
        }
      });
      setSwiper(swiper);
    }
  }, [mobSlider, loadedScriptStatus]);

  return (
    <>
      {isOpen && (
        <Lightbox
          mainSrc={lightboxMainSrc}
          clickOutsideToClose={false}
          nextSrc={
            lightboxNextSrc
              ? `${configCdnImagesSettings?.main}${lightboxNextSrc}`
              : null
          }
          prevSrc={
            lightboxPrevSrc
              ? `${configCdnImagesSettings?.main}${lightboxPrevSrc}`
              : null
          }
          animationDisabled
          imagePadding={0}
          onCloseRequest={lightboxOnCloseRequest}
          onMovePrevRequest={lightboxOnMovePrevRequest}
          onMoveNextRequest={lightboxOnMoveNextRequest}
        />
      )}
      <div className="item_preview_mobile">
        <div className="swiper" ref={mobSlider}>
          <div className="swiper-wrapper">
            {imageList?.map((item, id) => {
              if (isVideoLink(item?.image)) {
                return (
                  <video
                    ref={el => {
                      mobileVideoRefs.current[id] = el;
                    }}
                    width="100%"
                    height="100%"
                    controls
                    loop
                    preload="metadata"
                    muted
                  >
                    <source
                      src={`${configCdnImagesSettings?.main}${item}`}
                      type="video/mp4"
                    />
                  </video>
                );
              }

              return (
                <div
                  key={id}
                  className="item_preview_slide swiper-slide"
                  style={{ width: "100%", height: "100%" }}
                  onClick={() => {
                    !isLoadingImage && handleFullScreenImage(id);
                  }}
                >
                  <Img
                    decode={false}
                    loader={
                      <img
                        className="vw-100 mobile_image"
                        src={`${
                          isFromPLP
                            ? configCdnImagesSettings?.list
                            : configCdnImagesSettings?.thumb
                        }${item}`}
                      />
                    }
                    onLoad={() => setisLoadingImage(false)}
                    src={`${configCdnImagesSettings?.main}${item}`}
                    alt={product.title?.label}
                    height={1200}
                    width={800}
                  />
                </div>
              );
            })}
          </div>
          <div className="swiper-pagination"></div>
        </div>
        {videoIndex && activeSlide !== videoIndex && (
          <div className="playVideo" onClick={handleActiveVideoSlide} />
        )}
      </div>
    </>
  );
};

export default memo(MobileImage);
